import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminStores() {
  const [stores, setStores] = useState([]);
  const [form, setForm] = useState({ name: "", location: "" });
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/stores");
      setStores(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      await api.post("/stores", { name: form.name.trim(), location: form.location.trim() });
      setForm({ name: "", location: "" });
      toast.success("Store added");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.put(`/stores/${editing.id}`, { name: editing.name.trim(), location: editing.location || "" });
      setEditing(null);
      toast.success("Updated");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this store and all its entries?")) return;
    try {
      await api.delete(`/stores/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1100px]">
      <div className="mb-6">
        <div className="label-uc">Configuration</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Stores</h1>
      </div>

      <form onSubmit={add} className="bg-white border border-zinc-200 p-5 mb-6 grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid="store-add-form">
        <div>
          <Label className="label-uc">Store name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Papa John's — Camden" className="rounded-none border-zinc-300 mt-1.5" required data-testid="store-name-input" />
        </div>
        <div>
          <Label className="label-uc">Location</Label>
          <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
            placeholder="London NW1" className="rounded-none border-zinc-300 mt-1.5" data-testid="store-location-input" />
        </div>
        <Button type="submit" className="rounded-none bg-zinc-950 hover:bg-zinc-800 h-10" data-testid="store-add-button">
          <Plus className="w-4 h-4 mr-2" /> Add store
        </Button>
      </form>

      <div className="bg-white border border-zinc-200">
        <table className="w-full text-sm" data-testid="stores-table">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="px-4 py-2 label-uc">Name</th>
              <th className="px-4 py-2 label-uc">Location</th>
              <th className="px-4 py-2 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {stores.length === 0 && (
              <tr><td colSpan={3} className="px-4 py-12 text-center text-zinc-400">No stores yet.</td></tr>
            )}
            {stores.map((s) => (
              <tr key={s.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`store-row-${s.id}`}>
                <td className="px-4 py-2">
                  {editing?.id === s.id ? (
                    <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      className="rounded-none border-zinc-300 h-8" />
                  ) : s.name}
                </td>
                <td className="px-4 py-2 text-zinc-600">
                  {editing?.id === s.id ? (
                    <Input value={editing.location || ""} onChange={(e) => setEditing({ ...editing, location: e.target.value })}
                      className="rounded-none border-zinc-300 h-8" />
                  ) : (s.location || "—")}
                </td>
                <td className="px-4 py-2 text-right">
                  {editing?.id === s.id ? (
                    <div className="flex gap-2 justify-end">
                      <button onClick={saveEdit} className="text-emerald-600" data-testid={`store-save-${s.id}`}><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditing(null)} className="text-zinc-400"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEditing(s)} className="text-zinc-500 hover:text-zinc-900" data-testid={`store-edit-${s.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(s.id)} className="text-zinc-400 hover:text-[#DF1A22]" data-testid={`store-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
